import { v4 as uuidv4 } from "uuid";
import * as tillServices from "../services/tillServices";
import { getLocalMaxSequence, resolveInitialDocumentSequence } from "./documentSequenceUtils";
import { isParkedSeriesDocumentno } from "./cartIdentityGuard";
import { logSequenceEvent } from "./sequenceAudit";

/**
 * BUG-2468 — startup bill-number / cart-identity reconcile.
 *
 * Browser storage (localStorage + IndexedDB) is not written to disk on every
 * change. A power cut or hard crash can bring the POS back with the counter,
 * the cart and db.orders all rolled back to a moment before the last bills
 * (MNDPOS2 01-10: ₹1270 bill MND/2/205215 synced, then the restart restored
 * counter 205214 and the pre-checkout cart with that bill's sOrderID; the next
 * bill printed as 205215 again and the server dropped it as a repeat of the
 * known sOrderID). Every local guard (BUG-1477 mount check, DI-31, DI-33) reads
 * db.orders, which was rolled back too, so only the server can tell.
 *
 * Login already reconciles the counter with the server; a reload, Full Sync
 * reload (BUG-2385) or restart after a power cut never did. This runs on every
 * POS mount instead.
 */

const newSOrderID = () => uuidv4().replace(/-/g, "").toUpperCase();

// Storage can only roll back across a page load, so the cart part runs once per
// page load. Core re-mounting inside a running page (screen changes) must not
// re-issue a live cart's sOrderID.
// serverDone: a pass reached the server — nothing left to do in this page load.
// localDone:  the mount pass ran; later passes are retries for the server step only.
// trackedSOrderID: the restored cart's sOrderID after the mount pass; a retry only
//                  touches the cart while it is still that cart.
const pageLoad = { localDone: false, serverDone: false, trackedSOrderID: null };
export const getStartupPassState = () => pageLoad;
export const __resetStartupPassForTests = () => {
  pageLoad.localDone = false;
  pageLoad.serverDone = false;
  pageLoad.trackedSOrderID = null;
};

/**
 * Runs the next pending pass for this page load (mount pass first, then server
 * retries). Returns true when nothing is left to retry.
 */
export async function runStartupReconcilePass(params) {
  if (pageLoad.serverDone) return true;
  const isMount = !pageLoad.localDone;
  if (isMount) pageLoad.trackedSOrderID = params.cartStore.getState().cart?.sOrderID || null;
  const result = await reconcileStartupSequence({
    ...params,
    restoredSOrderID: pageLoad.trackedSOrderID,
    allowLocalRegenerate: isMount,
    phase: isMount ? "mount" : "retry",
  });
  pageLoad.localDone = true;
  if (result.cartAction !== "clear") pageLoad.trackedSOrderID = result.cartSOrderID;
  else pageLoad.trackedSOrderID = null;
  if (result.serverReached) pageLoad.serverDone = true;
  return result.serverReached;
}

/**
 * Carts that legitimately keep the identity of an existing record. Same
 * exemptions as cartIdentityGuard (Layaway/Advance, retrieved, Prepare Bill,
 * OCU, split, credit sale, parked series).
 */
export const isIdentityBoundCart = (cart, preorderPrefix) => {
  if (!cart) return true;
  if (cart.layAway === "Y" || cart.type === "Layaway" || cart.docType === "Advance" || cart.isAdvance === "Y") return true;
  if (cart.isRetrived === "Y" || cart.retrievedDocumentno) return true;
  if (cart.preparedBillOrigin === "Y" || cart.isPrepareBillCreate === "Y") return true;
  if (cart.statusType === "OCU" || cart.isSplit === "Y" || cart.isCreditSale === "Y" || cart.creditSale === "Y") return true;
  if (cart.isReturn === true || cart.isReturn === "Y") return true;
  // Opened from Sales History (carries the server order's sOrderID), parked, pre-order
  // page / pre-order, estimate and quotation carts.
  if (cart.salesHistory === "Y" || cart.parked === "Y") return true;
  if (cart.preOrder === "Y" || cart.isPreOrderPage) return true;
  if (cart.estimateId || cart.isQuotation) return true;
  // Restaurant table / KOT carts: sOrderID is bound to the table session.
  if (cart.tableId || cart.tableNo || cart.fbOrderId) return true;
  if (isParkedSeriesDocumentno(cart.documentno, preorderPrefix)) return true;
  return false;
};

const seqOf = (documentno, prefix) => {
  if (!documentno || !prefix || !documentno.startsWith(prefix)) return null;
  const n = parseInt(documentno.slice(prefix.length), 10);
  return Number.isNaN(n) ? null : n;
};

/** Counter after reconcile: never lower than what this device already holds. */
export const resolveStartupSequence = ({ persistedSeq, serverNextAssignedNumber, localMax }) =>
  Math.max(Number(persistedSeq) || 0, resolveInitialDocumentSequence(serverNextAssignedNumber, localMax));

/**
 * What to do with the restored cart.
 *  - "clear":      server already holds this cart's bill number and the cart carries
 *                  payments → snapshot of a sale completed before the restart (same
 *                  outcome as the BUG-1477 local check, which can no longer see it).
 *  - "renumber":   server already holds this cart's bill number but the cart is unpaid →
 *                  keep the items, fresh sOrderID + next number, and warn the cashier.
 *                  Not cleared: on a backend whose counter has drifted ahead of the
 *                  till (OFC counts orders, it does not track the number) this can be
 *                  a genuine sale in progress.
 *  - "regenerate": plain unpaid cart the server does not know (or cannot be asked
 *                  about) → fresh sOrderID. Nothing is bound to an unpaid plain cart's
 *                  sOrderID yet, so this is always safe, and it is the only defence
 *                  while the server cannot be reached.
 *  - "none":       empty cart, identity-bound cart, or paid cart the server does not
 *                  know (its sOrderID may already be bound to a UPI QR / gift card).
 */
export const decideStartupCartAction = ({ cart, preorderPrefix, prefix, serverLastConsumed }) => {
  const hasItems = (cart?.items?.length || 0) > 0;
  const hasPayments = (cart?.payments?.length || 0) > 0;
  if (!hasItems && !hasPayments) return "none";
  if (isIdentityBoundCart(cart, preorderPrefix)) return "none";
  const cartSeq = seqOf(cart.documentno, prefix);
  const consumedOnServer = serverLastConsumed != null && cartSeq != null && cartSeq <= serverLastConsumed;
  if (consumedOnServer) return hasPayments ? "clear" : "renumber";
  return hasPayments ? "none" : "regenerate";
};

const fetchServerFloor = async (userData, cwrTillID) => {
  try {
    if (!userData || !cwrTillID || typeof tillServices.getTillSequenceFloor !== "function") return null;
    return await tillServices.getTillSequenceFloor(userData, cwrTillID);
  } catch {
    return null;
  }
};

/**
 * One reconcile pass. Returns { serverReached, cartAction }.
 *
 * @param {Object} p
 * @param {Object} p.db
 * @param {Object} p.cartStore       useCartStore
 * @param {Object} p.sessionStore    useSessionStore
 * @param {Object} p.tillData
 * @param {string} p.userData        username (Enterprise) or email, as Till.js uses for tillData()
 * @param {string} p.restoredSOrderID sOrderID the cart had when the POS mounted
 * @param {boolean} p.allowLocalRegenerate  false on retry passes (the local step already ran at mount)
 * @param {Function} [p.setReactSequence]   Core's setDocumnetSequence (the value checkout reads)
 * @param {Function} [p.onCartCleared]      UI notice for the "clear" case
 * @param {Function} [p.onCartRenumbered]   UI notice for the "renumber" case (oldNo, newNo)
 * @param {Function} [p.fetchFloor]         test seam; defaults to the server query
 * @param {string} p.phase  "mount" | "retry"
 */
export async function reconcileStartupSequence({
  db,
  cartStore,
  sessionStore,
  tillData,
  userData,
  restoredSOrderID,
  allowLocalRegenerate = true,
  setReactSequence,
  onCartCleared,
  onCartRenumbered,
  fetchFloor = fetchServerFloor,
  phase = "mount",
  cartChecks = true,
}) {
  const cwrTill = tillData?.tillAccess?.cwrTill;
  const prefix = cwrTill?.prefix;
  if (!prefix) return { serverReached: false, cartAction: "none" };
  const preorderPrefix = sessionStore.getState().preorderSequence?.prefix;

  const floor = await fetchFloor(userData, cwrTill.cwrTillID);
  const serverReached = !!floor;

  // Cart decision uses the cart as restored at mount; a cashier who already
  // started a new sale since then is left alone.
  const cart = cartStore.getState().cart;
  const sameRestoredCart = cartChecks && !!cart?.sOrderID && cart.sOrderID === restoredSOrderID;

  let chosenSeq = null;
  if (serverReached) {
    const localMax = await getLocalMaxSequence(db, prefix).catch(() => 0);
    const persistedSeq = sessionStore.getState().documentSequence || 0;
    chosenSeq = resolveStartupSequence({ persistedSeq, serverNextAssignedNumber: floor.nextAssignedNumber, localMax });
    if (chosenSeq > persistedSeq) {
      sessionStore.getState().setDocumentSequence(chosenSeq);
      setReactSequence?.(chosenSeq);
    }
    logSequenceEvent(
      "init",
      { source: `pos-${phase}`, prefix, serverSeq: floor.nextAssignedNumber, localMax, chosen: chosenSeq, prevCounter: persistedSeq },
      db
    );
  }

  let cartAction = "none";
  if (sameRestoredCart) {
    const serverLastConsumed = serverReached && floor.nextAssignedNumber > 0 ? floor.nextAssignedNumber - 1 : null;
    cartAction = decideStartupCartAction({ cart, preorderPrefix, prefix, serverLastConsumed });
    // The mount pass already gave this cart a fresh sOrderID; a retry only acts on "clear" / "renumber".
    if (cartAction === "regenerate" && !allowLocalRegenerate) cartAction = "none";

    const liveSeq = sessionStore.getState().documentSequence || 0;
    const previewNo = `${prefix}${liveSeq + 1}`;
    if (cartAction === "regenerate" || cartAction === "renumber") {
      const freshId = newSOrderID();
      cartStore.getState().setCart((prev) => (prev.sOrderID === restoredSOrderID ? { ...prev, sOrderID: freshId, documentno: previewNo } : prev));
      logSequenceEvent(
        "stale-identity",
        { staleSOrderID: restoredSOrderID, newSOrderID: freshId, existingDocumentno: cart.documentno, newDocumentno: previewNo, itemCount: cart.items?.length || 0, flow: cartAction === "renumber" ? `startup-${phase}-server` : `startup-${phase}`, serverReached },
        db
      );
      if (cartAction === "renumber") onCartRenumbered?.(cart.documentno, previewNo);
    } else if (cartAction === "clear") {
      cartStore.getState().clearCart({ documentno: previewNo });
      logSequenceEvent(
        "stale-identity",
        { staleSOrderID: restoredSOrderID, existingDocumentno: cart.documentno, itemCount: cart.items?.length || 0, flow: `startup-${phase}-server`, cleared: true },
        db
      );
      onCartCleared?.(cart.documentno);
    } else if (serverReached && (cart.items?.length || 0) === 0 && !isIdentityBoundCart(cart, preorderPrefix)) {
      // Empty cart: only its preview number can be stale.
      if (cart.documentno !== previewNo) cartStore.getState().setCart((prev) => (prev.sOrderID === restoredSOrderID && !(prev.items?.length) ? { ...prev, documentno: previewNo } : prev));
    }
  }

  return { serverReached, cartAction, chosenSeq, cartSOrderID: cartStore.getState().cart?.sOrderID };
}
